import { useState, useMemo } from 'react';
import { inferTableStructure } from '../../utils/tableInference';
import PreviewTable from './PreviewTable';

function RadarMappingPanel({ columns, rows, initialMapping, onConfirm, onBack, ui }) {
  const numericColumns = columns.filter((c) => c.type === 'number');
  const stringColumns = columns.filter((c) => c.type === 'string');
  const inferred = useMemo(() => inferTableStructure(rows, columns, 'radar'), [rows, columns]);

  const [seriesCol, setSeriesCol] = useState(initialMapping.series || stringColumns[0]?.name || '');
  const [selectedAxes, setSelectedAxes] = useState(() => {
    if (Array.isArray(initialMapping.axisColumns)) return initialMapping.axisColumns;
    return numericColumns.map((c) => c.name);
  });

  const toggleAxis = (name) => {
    setSelectedAxes((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  const canConfirm = Boolean(seriesCol) && selectedAxes.length >= 3;

  const handleConfirm = () => {
    onConfirm({ series: seriesCol, axisColumns: selectedAxes });
  };

  return (
    <div>
      {inferred?.notes && (
        <div className="border p-2.5 mb-3 text-xs leading-relaxed" style={{ color: ui.textSecondary, borderColor: ui.borderSecondary, backgroundColor: ui.bgSecondary }}>
          <span className="font-medium" style={{ color: ui.text }}>Auto-detected: </span>{inferred.notes}
        </div>
      )}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">Name / label column</div>
          <p className="mb-1" style={{ color: ui.textSecondary }}>Which column names each item (e.g. model name)?</p>
          <select
            value={seriesCol}
            onChange={(e) => setSeriesCol(e.target.value)}
            className="w-full text-xs border py-1 px-2"
            style={{ borderColor: ui.borderSecondary }}
          >
            <option value="">Select a column</option>
            {stringColumns.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="border p-3 mb-4" style={{ borderColor: ui.borderSecondary }}>
        <div className="text-xs font-medium mb-1" style={{ color: ui.text }}>Axes (select at least 3)</div>
        <p className="text-xs mb-2" style={{ color: ui.textSecondary }}>Each selected column becomes an axis on the radar chart.</p>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          {numericColumns.map((c) => (
            <label key={c.name} className="flex items-center gap-2 text-xs" style={{ color: ui.text }}>
              <input type="checkbox" checked={selectedAxes.includes(c.name)} onChange={() => toggleAxis(c.name)} />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
        {selectedAxes.length < 3 && (
          <p className="text-xs mt-2" style={{ color: ui.textSecondary }}>Radar charts need at least 3 axes.</p>
        )}
      </div>

      <PreviewTable columns={columns} rows={rows} ui={ui} />
      <div className="flex gap-2">
        <button onClick={onBack} className="flex-1 text-xs border py-2 ui-hover ui-border" style={{ borderColor: ui.border, color: ui.text }}>&larr; Back</button>
        <button onClick={handleConfirm} disabled={!canConfirm} className={`flex-1 text-xs border py-2 font-medium ui-border ${!canConfirm ? 'opacity-40 cursor-not-allowed' : 'ui-hover'}`} style={{ borderColor: ui.border, color: ui.text }}>
          Confirm &rarr;
        </button>
      </div>
    </div>
  );
}

export default RadarMappingPanel;
